import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router';
import { Controller, useForm } from 'react-hook-form'
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import ConsultaDocumentoService from 'services/ConsultaDocumentoService';
import COCService from 'services/COCService';
import KskForm from 'ksk/styledComponents/components/KskForm/KskForm';
import KskToast from 'ksk/styledComponents/components/KskToast/KskToast';
import KskTooltip from 'ksk/styledComponents/components/KskPopover/KskTooltip';
import useWindowScroll from 'ksk/hooks/useWindowScroll.jsx';
import useWindowSize from 'ksk/hooks/useWindowSize';
import { formatos } from 'utils/maskUtils';
import { getFeature } from 'config/features.config';
import { buildUrlSearchParams } from 'utils/utils';
import { validateIdSiconfi } from 'utils/validation-utils';
import { identificadorSiconfiFormatter } from 'utils/formatters.js';
import PVLService from 'services/PVLService.js';
import GarantiaService from 'services/GarantiaService.js';
import { BrButton } from '@govbr-ds/react-components'

const formID = `busca-idsiconfi-form`;

function BuscaRapidaIdSiconfi(){
    
    const form = useForm({
        mode:'onTouched',
        defaultValues:{
            idSiconfi:""
        }
    })
    
    const [scrollY, scrollTop] = useWindowScroll();
    const { isPhone } = useWindowSize();
    
    const handleOnFocuBuscaRapidaIdSiconfi = e => {
        if(e.ctrlKey && e.shiftKey && (e.key === 'F'||e.key === 'f')){
            document.getElementById(formID).elements[0].focus();
            scrollTop();
        }
    }
    
    useEffect(() => {
        window.addEventListener("keydown", handleOnFocuBuscaRapidaIdSiconfi);
    }, [])

    const navigate = useNavigate();

    const doSearchByService = async (filtroIdSiconfi,service)=>{
        const searchStr = buildUrlSearchParams(filtroIdSiconfi);
        const response = await service.paginatedSearch({searchStr:searchStr,page:0,linesPerPage:1})
        if(response.data.totalElements>0){
          return response;
        }
        else{
            throw new Error(`Identificador Siconfi ${identificadorSiconfiFormatter(filtroIdSiconfi.identificadorSiconfi)} não localizado`);
        }
    }

    const doSearch = async () => {
        try {
            const idSiconfi = form?.getValues("idSiconfi");
            if(idSiconfi==null||""===idSiconfi){
                throw new Error('Campo obrigatório.');
            }else{
                validateIdSiconfi(idSiconfi);
                const tipoIdSiconfi = idSiconfi.substring(4,6);
                switch (tipoIdSiconfi) {
                    case "00":// COC
                        const response00 = await doSearchByService({identificadorSiconfi:idSiconfi},COCService);
                        const id00 = response00.data.content[0].id
                        const urlFeature00 = getFeature('COC').route.url
                        navigate(urlFeature00+"/edit/"+id00);                    
                        break;
                    case "01":// Garantia
                        const response01 = await doSearchByService({identificadorSiconfi:idSiconfi},GarantiaService);
                        const id01 = response01.data.content[0].id
                        const urlFeature01 = getFeature('GARANTIA').route.url
                        navigate(urlFeature01+"/edit/"+id01);                    
                        break;
                    case "02":// PVL
                        const response02 = await doSearchByService({identificadorSiconfi:idSiconfi},PVLService);
                        const id02 = response02.data.content[0].id
                        const urlFeature02 = getFeature('PVL').route.url
                        navigate(urlFeature02+"/edit/"+id02);                    
                        break;
                    case "03":// Documento interno
                        const response03 = await doSearchByService({idIdentificador:idSiconfi},ConsultaDocumentoService);
                        const id03 = response03.data.content[0].identificadorCompleto
                        const urlFeature03 = getFeature('CONSULTA_PUBLICA_DOCUMENTO').route.url
                        navigate(urlFeature03+"/"+id03);        
                        break;
                    case "04":// Documento externo
                        const response04 = await doSearchByService({idIdentificador:idSiconfi},ConsultaDocumentoService);
                        const id04 = response04.data.content[0].identificadorCompleto
                        const urlFeature04 = getFeature('CONSULTA_PUBLICA_DOCUMENTO').route.url
                        navigate(urlFeature04+"/"+id04);        
                        break;
                    default:
                        throw new Error("O tipo do identificador siconfi informado não permite busca rápida.");
                }

            }
            form.reset()            
            document.activeElement.blur();
        } catch (error) {
            toast.error(<KskToast error mainMessage={"Identificador Siconfi"} details={error.message} />);
        }
    }
    
    const onValidationError = useCallback((e) => {
        toast.error(<KskToast error mainMessage={"Identificador siconfi"} details="Erro no preenchimento, corrija os erros e tente novamente" />);
        console.error(`Identificador siconfi. Erro no preenchimento, corrija os erros e tente novamente:`, e);
    }, []);
    
    // caso esse componente seja disponbilizado para perfil 'Publico', deve ser incorporado o uso do captcha
    // const { captchaRef, executeWithCaptcha } = useCaptcha();
    const search = useCallback(form.handleSubmit(doSearch, onValidationError),[])
    
    return (
        <KskForm onSubmit={ search } id={formID} submitOnShiftEnter>
            <KskTooltip
                title="Foco: Ctrl + Shift + F" arrow placement={isPhone ? 'bottom' : 'left'}
                disableFocusListener={true} disableTouchListener={true}>
                <div className="br-input has-icon">
                    <Controller
                        control={form.control}
                        name="idSiconfi"
                        render={({ field }) => (
                            <InputMask
                                id="searchbox"
                                name={field.name}
                                placeholder="Identificador Siconfi"
                                aria-label="Identificador Siconfi"
                                mask={formatos.identificadorsiconfi.mask}
                                maintainMaskedValue
                                value={field.value}
                                inputRef={field.ref}
                                onBlur={field.onBlur}
                                onChange={event => field.onChange(formatos.identificadorsiconfi.unmask(event.target.value))}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' && (event.shiftKey || form.formState.isSubmitting)) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        if (!form.formState.isSubmitting) {
                                            event.currentTarget.form.requestSubmit();
                                        }
                                    }
                                }}
                            />
                        )}
                    />
                    <BrButton
                        id="btn-pesquisar"
                        icon={'search'}
                        circle
                        small
                        type="submit"
                        aria-label="Pesquisar"
                        aria-busy={form.formState.isSubmitting}
                        loading={form.formState.isSubmitting}
                        disabled={form.formState.isSubmitting}
                    />
                </div>
            </KskTooltip>
        </KskForm>
    )
} 

export default BuscaRapidaIdSiconfi;